import React, { useState, useRef, useCallback } from 'react';
import { Usb, Network as NetworkIcon, Printer } from 'lucide-react';
import * as printerApi from '@/services/printerApi';
import * as networkPrintApi from '@/services/networkPrintApi';
import NetworkPrinterDialog from '@/components/print/NetworkPrinterDialog';

// Per-screen label print destination. Each screen passes a unique `screenKey`
// so its USB/Network choice + selected network printer are remembered
// independently (e.g. combo stations stay on USB while other screens use network).
export function useLabelPrinter(screenKey = 'default') {
  const METHOD_K = `lp_method::${screenKey}`;
  const PRINTER_K = `lp_printer::${screenKey}`;

  const [method, setMethodState] = useState(() => localStorage.getItem(METHOD_K) || 'usb');
  const [netPrinter, setNetPrinterState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PRINTER_K) || 'null'); } catch (_) { return null; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerResolve = useRef(null);
  const usbDeviceRef = useRef(null);

  const setMethod = useCallback((m) => {
    setMethodState(m);
    localStorage.setItem(METHOD_K, m);
  }, [METHOD_K]);

  const setNetPrinter = useCallback((sel) => {
    setNetPrinterState(sel);
    if (sel) localStorage.setItem(PRINTER_K, JSON.stringify(sel));
    else localStorage.removeItem(PRINTER_K);
  }, [PRINTER_K]);

  const openPicker = useCallback((force = false) => {
    if (!force && netPrinter) return Promise.resolve(netPrinter);
    return new Promise((resolve) => {
      pickerResolve.current = resolve;
      setPickerOpen(true);
    });
  }, [netPrinter]);

  const handlePicked = (sel) => {
    const selection = { agentId: sel.agentId, printerName: sel.printerName, agentName: sel.agentName };
    setNetPrinter(selection);
    networkPrintApi.setPrintPreference({ agentId: sel.agentId, printerName: sel.printerName }).catch(() => {});
    setPickerOpen(false);
    if (pickerResolve.current) { pickerResolve.current(selection); pickerResolve.current = null; }
  };

  const handleClose = (open) => {
    if (open) return;
    setPickerOpen(false);
    if (pickerResolve.current) { pickerResolve.current(null); pickerResolve.current = null; }
  };

  const getUsbDevice = useCallback(async (force = false) => {
    if (!force && usbDeviceRef.current) return usbDeviceRef.current;
    if (!force && 'usb' in navigator) {
      try {
        const devs = await navigator.usb.getDevices();
        if (devs && devs.length) { usbDeviceRef.current = devs[0]; return devs[0]; }
      } catch (_) { /* fall through to prompt */ }
    }
    const dev = await printerApi.requestUSBPrinter();
    usbDeviceRef.current = dev;
    return dev;
  }, []);

  // Unified send for THIS screen's destination.
  const printBlob = useCallback(async (commands, { labelType = null, meta = null, forcePicker = false, copies = 1 } = {}) => {
    if (!commands) return { success: false, error: 'Nothing to print' };
    if (method === 'network') {
      let sel = netPrinter;
      if (forcePicker || !sel) {
        // A central operation route means no manual pick is needed — only
        // open the picker for unassigned operations (or an explicit re-pick).
        let hasRoute = false;
        if (!forcePicker) {
          try { hasRoute = (await networkPrintApi.getOperationRoute(screenKey)).assigned; } catch (_) { /* fall through */ }
        }
        if (!hasRoute) {
          sel = await openPicker(true);
          if (!sel) return { success: false, error: 'No printer selected' };
        }
      }
      try {
        return await networkPrintApi.printViaAgent({
          agentId: sel?.agentId, printerName: sel?.printerName, payload: commands, copies, labelType, meta,
          operationKey: screenKey,
        });
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || e.message || 'Network print error' };
      }
    }
    try {
      const device = await getUsbDevice(forcePicker);
      await printerApi.sendToUSBPrinter(device, commands);
      return { success: true };
    } catch (e) {
      usbDeviceRef.current = null;
      return { success: false, error: e.message || 'USB print error' };
    }
  }, [method, netPrinter, openPicker, getUsbDevice]);

  // Toggle bound to THIS screen's state. `dark` for dark headers.
  const MethodBar = useCallback(({ className = '', dark = false }) => {
    const activeCls = dark ? 'bg-white text-[#1A1F2E]' : 'bg-[#2D5F4F] text-white';
    const idleCls = dark ? 'text-[#E8DCC8] hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100';
    return (
      <div className={`inline-flex items-center gap-2 ${className}`} data-testid="label-print-method-bar">
        <div className={`flex rounded-md overflow-hidden border ${dark ? 'border-white/25' : 'border-gray-300'}`}>
          <button type="button" onClick={() => setMethod('usb')}
            className={`px-2.5 py-1 text-xs flex items-center gap-1 ${method === 'usb' ? activeCls : idleCls}`}
            data-testid="label-method-usb"><Usb className="w-3 h-3" /> USB</button>
          <button type="button" onClick={() => setMethod('network')}
            className={`px-2.5 py-1 text-xs flex items-center gap-1 ${method === 'network' ? activeCls : idleCls}`}
            data-testid="label-method-network"><NetworkIcon className="w-3 h-3" /> Network</button>
        </div>
        {method === 'network' && (
          <button type="button" onClick={() => openPicker(true)}
            className={`text-xs flex items-center gap-1 underline underline-offset-2 ${dark ? 'text-cyan-300' : 'text-[#2D5F4F]'}`}
            data-testid="label-select-network-printer">
            <Printer className="w-3 h-3" /> {netPrinter ? netPrinter.printerName : 'Select printer'}
          </button>
        )}
      </div>
    );
  }, [method, netPrinter, setMethod, openPicker]);

  const pickerDialog = (
    <NetworkPrinterDialog open={pickerOpen} onOpenChange={handleClose} onConfirm={handlePicked} title="Select a network printer" />
  );

  return { method, setMethod, netPrinter, setNetPrinter, openPicker, printBlob, MethodBar, pickerDialog };
}

// Kept as a passthrough for backward-compat with the App.js wrapper.
export const LabelPrinterProvider = ({ children }) => <>{children}</>;

export default LabelPrinterProvider;
